import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import {
  type AbstractControl,
  type FormControl,
  FormsModule,
  ReactiveFormsModule,
  type ValidationErrors,
  type ValidatorFn,
} from '@angular/forms';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import { HelperTextComponent } from '@bizzdesign/ui-helper-text';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { InputGroupModule } from 'primeng/inputgroup';

@Component({
  selector: 'bd-enum-options-editor',
  imports: [
    NgClass,
    SharedI18nModule,
    FormsModule,
    ReactiveFormsModule,
    AutoCompleteModule,
    InputGroupModule,
    HelperTextComponent,
  ],
  templateUrl: './enum-options-editor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnumOptionsEditorComponent {
  control = input.required<FormControl<string[] | null>>();
  enumOptionsControl = computed<FormControl<string[] | null>>(() => {
    const control = this.control();
    control.setValidators([
      this.uniqueValidator(),
      this.atLeastTwoValuesValidator(),
    ]);
    return control;
  });

  get errorKeys(): string[] {
    const errors = this.enumOptionsControl()?.errors;
    return errors ? Object.keys(errors) : [];
  }

  uniqueValidator(): ValidatorFn {
    return (control: AbstractControl<string[]>): ValidationErrors | null => {
      const values = control.value?.map((v) => v.toLowerCase()) || [];
      const hasDuplicates = new Set(values).size !== values.length;
      return hasDuplicates ? { unique: true } : null;
    };
  }

  formatEnumValues() {
    const values = this.enumOptionsControl().value || [];
    const formattedValue = values.map((entry) =>
      entry.trim().replace(/\s+/g, ' '),
    );
    if (formattedValue.toString() !== values.toString()) {
      this.enumOptionsControl().setValue(formattedValue);
      this.enumOptionsControl().updateValueAndValidity();
    }
  }

  atLeastTwoValuesValidator(): ValidatorFn {
    return (control: AbstractControl<string[]>): ValidationErrors | null => {
      const values = control.value || [];
      return values.length > 1 ? null : { required: true };
    };
  }
}
